import { useCallback } from 'react';
import { postData } from "../func/api";
import { COMMON_ROW_RESET_FIELDS, DATA_ROW_RESET_FIELDS, NOT_REPORT_RESET_FIELDS, INTAKE_STOP_RESET_FIELDS, DRAINAGE_RESET_FIELDS, CONTINUE_RESET_FIELDS, EMERGENCY_INSPECTION_RESET_FIELDS } from '../const/suiro_fields';
import { SELECTED_DRAINAGE_POWERPLANT } from "../const/config";
import { useAuth } from '../contexts/AuthContext';

export const useSuiroHandlers = (suiroExtractList, setSuiroExtractList, toggleColumn) => {
  const { currentUser } = useAuth();
  const firstRow = suiroExtractList && suiroExtractList.length ? suiroExtractList[0] : {};

  const clearAllRows = useCallback((isRequiredParam) => {
    const dataKeys = DATA_ROW_RESET_FIELDS;
    const commonOnlyKeys = COMMON_ROW_RESET_FIELDS.filter(k => !DATA_ROW_RESET_FIELDS.includes(k));

    if (!suiroExtractList || !suiroExtractList.length) return;

    const targetUuids = suiroExtractList.map(r => r.report_progress_powerplant_uuid);
    const prevValues = suiroExtractList.map(r => ({ ...r }));

    const getResetValue = (key, sampleValue) => {
      if (key === 'is_emergency_inspection') return null; // 特定のフィールドは null
      return typeof sampleValue === 'boolean' ? false : '';
    };

    setSuiroExtractList(prev =>
      prev.map(row => {
        // UUIDが、今回リセット対象にしたUUIDリストに含まれていなければ何もしない
        if (!targetUuids.includes(row.report_progress_powerplant_uuid)) {
          return row;
        }

        const cloned = { ...row };
        [...dataKeys, ...commonOnlyKeys].forEach(k => {
          if (k in cloned) {
            cloned[k] = getResetValue(k, cloned[k]);
          }
        });
        return cloned;
      })
    );

    const bulkPayload = {
      bulk: suiroExtractList.map(row => {
        const updates = {};
        [...dataKeys, ...commonOnlyKeys].forEach(k => {
          updates[k] = getResetValue(k, row[k]);
        });
        return {
          uuid: row.report_progress_powerplant_uuid,
          updates: updates,
          updated_by: null // リセット時は履歴を追わない仕様に合わせる
        };
      })
    };

    postData('/api/post_update_report_progress_powerplant_bulk', bulkPayload)
      .then(res => {
        // 成功判定（バルク更新のレスポンス形式に合わせる）
        if (!res || res.success === false) {
          setSuiroExtractList(() => prevValues);
          console.error('clearAllRows failed', res);
        }
        // res.success === true の場合は既にUI更新済みなので何もしない
      })
      .catch(err => {
        console.error('clearAllRows error', err);
        setSuiroExtractList(() => prevValues);
      });
  }, [suiroExtractList, setSuiroExtractList]);


  // 各行のリセットボタン押下時処理
  const handleReset = useCallback((uuid, resetFields) => {
    const resetKeys = resetFields;

    if (!suiroExtractList || !suiroExtractList.length) return;

    const prevValues = suiroExtractList.map(r => ({ ...r }));

    const matchingPrev = prevValues.find(r => (r.uuid && r.uuid === uuid) || (r.id && String(r.id) === String(uuid)));
    if (!matchingPrev) return;

    const facility_uuid = matchingPrev.uuid || null;
    const report_progress_uuid = matchingPrev.report_progress_powerplant_uuid || null;

    const updates = {};
    resetKeys.forEach(k => {
      if (k === 'is_emergency_inspection') {
        updates[k] = null;
      } else {
        const sampleVal = matchingPrev[k];
        updates[k] = typeof sampleVal === 'boolean' ? false : '';
      }
    });

    const payload = {
      uuid: report_progress_uuid,
      facility_uuid,
      updates,
      updated_by: null
    };

    postData('/api/post_update_report_progress_powerplant_bulk', payload)
      .then(res => {
        // 成功
        setSuiroExtractList(prev =>
          prev.map(row => {
            const matches = (row.uuid && row.uuid === uuid);
            if (!matches) return row;
            const newRow = { ...row };
            resetKeys.forEach(k => {
              if (k in newRow) {
                if (k === 'is_emergency_inspection') {
                  newRow[k] = null;
                } else {
                  newRow[k] = typeof newRow[k] === 'boolean' ? false : '';
                }
              }
            });
            return newRow;
          })
        );
      })
      .catch(err => {
        console.error('failed to bulk-reset report_progress_powerplant:', err);
        // ロールバック：前の配列を復元
        setSuiroExtractList(() => prevValues.map(r => ({ ...r })));
      });
  }, [suiroExtractList, setSuiroExtractList]);

  // 各行のチェックボックスコンポーネント変更時処理
  const handleCheckboxChange = useCallback((uuid, field, is_all, option = null) => {
    const currentRow = suiroExtractList?.find(r => (r.uuid === uuid) || (String(r.id) === String(uuid)));
    if (!currentRow) {
      toggleColumn(field, true);
      return;
    }

    const SPECIAL_DRAINAGE_FIELD = 'is_selected_drainage_powerplant';
    const prevValues = suiroExtractList.map(r => ({ ...r }));

    // --- 1. 特殊なUUIDフィールド (3択) の処理 ---
    if (field === SPECIAL_DRAINAGE_FIELD) {
      const currUuidVal = currentRow[field] || '';
      const mapping = {
        INTAKESTOP: SELECTED_DRAINAGE_POWERPLANT.INTAKESTOP,
        DRAINAGE: SELECTED_DRAINAGE_POWERPLANT.DRAINAGE,
        CONTINUE: SELECTED_DRAINAGE_POWERPLANT.CONTINUE
      };
      const targetUuid = mapping[option] || '';
      const newUuid = (currUuidVal === targetUuid) ? '' : targetUuid;

      // UI更新（UUIDをセット）
      setSuiroExtractList(prev => prev.map(row => {
        if (!is_all && row.uuid !== uuid) return row;
        return { ...row, [field]: newUuid, [`${field}_by`]: newUuid ? (currentUser?.uuid || currentUser?.id) : null };
      }));

      // API送信（UUIDをセット）
      const targetRows = is_all ? suiroExtractList : [currentRow];
      const payload = {
        bulk: targetRows.map(row => ({
          uuid: row.report_progress_powerplant_uuid,
          updates: { [field]: newUuid },
          updated_by: newUuid ? (currentUser?.uuid || currentUser?.id) : null
        }))
      };

      postData('/api/post_update_report_progress_powerplant_bulk', payload)
        .then(res => {
          if (!res || res.success === false) setSuiroExtractList(prevValues);
        }).catch(() => setSuiroExtractList(prevValues));

      return;
    }

    // --- 2. 通常のチェックボックス (true/false) の処理 ---
    const prevVal = !!currentRow[field];
    const newVal = !prevVal;

    setSuiroExtractList(prev => prev.map(row => {
      if (!is_all && row.uuid !== uuid) return row;
      return { ...row, [field]: newVal, [`${field}_by`]: newVal ? (currentUser?.uuid || currentUser?.id) : null };
    }));

    const targetRows = is_all ? suiroExtractList : [currentRow];
    const payload = {
      bulk: targetRows.map(row => ({
        uuid: row.report_progress_powerplant_uuid,
        updates: { [field]: newVal },
        updated_by: newVal ? (currentUser?.uuid || currentUser?.id) : null
      }))
    };

    postData('/api/post_update_report_progress_powerplant_bulk', payload)
      .then(res => {
        if (!res || res.success === false) setSuiroExtractList(prevValues);
      }).catch(() => setSuiroExtractList(prevValues));

  }, [suiroExtractList, currentUser, setSuiroExtractList, toggleColumn]);

  // 各行のテキストコンポーネント変更時処理
  const handleTextFieldChange = useCallback((uuid, value, field, is_all) => {
    const currentRow = suiroExtractList?.find(r => (r.uuid === uuid) || (String(r.id) === String(uuid)));
    if (!currentRow) {
      toggleColumn?.(field, true);
      return;
    }

    const prevValues = suiroExtractList.map(r => ({ ...r }));
    const newVal = (value === null || value === undefined) ? '' : value;

    // --- 1. 更新対象のUUIDリストを確定 ---
    const targetUuids = is_all
      ? suiroExtractList.map(r => r.report_progress_powerplant_uuid)
      : [currentRow.report_progress_powerplant_uuid];

    // --- 2. UI更新 (Optimistic Update) ---
    setSuiroExtractList(prev => prev.map(row => {
      if (!targetUuids.includes(row.report_progress_powerplant_uuid)) return row;

      let updatedRow = { ...row, [field]: newVal };

      // 特殊フィールド連動ロジック: 点検員
      if (field === 'canalinspector1' || field === 'canalinspector2') {
        const v1 = field === 'canalinspector1' ? newVal : (row.canalinspector1 || '');
        const v2 = field === 'canalinspector2' ? newVal : (row.canalinspector2 || '');
        updatedRow.is_canalinspector = !!v1 || !!v2;
      }
      // 特殊フィールド連動ロジック: 内部点検者
      else if (field === 'perform_internal_inspection_user1' || field === 'perform_internal_inspection_user2') {
        const v1 = field === 'perform_internal_inspection_user1' ? newVal : (row.perform_internal_inspection_user1 || '');
        const v2 = field === 'perform_internal_inspection_user2' ? newVal : (row.perform_internal_inspection_user2 || '');
        updatedRow.is_perform_internal_inspection_user = !!v1 || !!v2;
      }

      // 担当者情報の更新 (値がある時だけIDを入れる)
      if (newVal !== '') {
        updatedRow[`${field}_by`] = currentUser?.id || currentUser?.uuid || null;
      } else {
        updatedRow[`${field}_by`] = null;
      }

      return updatedRow;
    }));

    // --- 3. APIペイロード作成 ---
    const bulkUpdates = suiroExtractList
      .filter(row => targetUuids.includes(row.report_progress_powerplant_uuid))
      .map(row => {
        const updates = { [field]: newVal };

        // 連動フィールドの値を算出（UI更新ロジックと同期）
        if (field === 'canalinspector1' || field === 'canalinspector2') {
          const v1 = field === 'canalinspector1' ? newVal : (row.canalinspector1 || '');
          const v2 = field === 'canalinspector2' ? newVal : (row.canalinspector2 || '');
          updates.is_canalinspector = !!v1 || !!v2;
        } else if (field === 'perform_internal_inspection_user1' || field === 'perform_internal_inspection_user2') {
          const v1 = field === 'perform_internal_inspection_user1' ? newVal : (row.perform_internal_inspection_user1 || '');
          const v2 = field === 'perform_internal_inspection_user2' ? newVal : (row.perform_internal_inspection_user2 || '');
          updates.is_perform_internal_inspection_user = !!v1 || !!v2;
        }

        return {
          uuid: row.report_progress_powerplant_uuid,
          updates: updates,
          updated_by: newVal !== '' ? (currentUser?.id || currentUser?.uuid || null) : null
        };
      });

    // --- 4. 送信 ---
    postData('/api/post_update_report_progress_powerplant_bulk', { bulk: bulkUpdates })
      .then(res => {
        if (!res || res.success === false) {
          setSuiroExtractList(prevValues);
          console.error(`${field} update failed`, res);
        }
      })
      .catch(err => {
        console.error(`${field} update error`, err);
        setSuiroExtractList(prevValues);
      });

  }, [suiroExtractList, currentUser, setSuiroExtractList, toggleColumn]);

  // 各行の時間コンポーネント変更時処理
  const handleRowTimeChange = useCallback((uuid, field, newValue, is_all) => {
    // 1. 値の成形
    const isNull = newValue === null;
    const formatted = isNull ? '' : (newValue ? (typeof newValue.format === 'function' ? newValue.format('HH:mm') : String(newValue)) : '');

    const currentRow = suiroExtractList?.find(r => (r.uuid === uuid) || (String(r.id) === String(uuid)));
    if (!currentRow) {
      toggleColumn?.(field, true);
      return;
    }

    const prevValues = suiroExtractList.map(r => ({ ...r }));

    // 2. 更新対象のUUIDリストを確定 (表示されている範囲に限定)
    const targetUuids = is_all
      ? suiroExtractList.map(r => r.report_progress_powerplant_uuid)
      : [currentRow.report_progress_powerplant_uuid];

    // 3. UI更新 (Optimistic Update)
    setSuiroExtractList(prev => prev.map(row => {
      if (!targetUuids.includes(row.report_progress_powerplant_uuid)) return row;

      let updatedRow = { ...row, [field]: formatted };

      // 担当者情報の更新 (値がある時だけIDを入れる)
      const updatedByField = `${field}_by`;
      if (formatted !== '') {
        updatedRow[updatedByField] = currentUser?.id || currentUser?.uuid || null;
      } else {
        updatedRow[updatedByField] = null;
      }

      return updatedRow;
    }));

    // 4. APIペイロード作成 (常に bulk 形式を使用)
    const bulkUpdates = suiroExtractList
      .filter(row => targetUuids.includes(row.report_progress_powerplant_uuid))
      .map(row => {
        return {
          uuid: row.report_progress_powerplant_uuid,
          updates: { [field]: formatted === '' ? null : formatted }, // DBへは空文字ではなく null を送る
          updated_by: formatted !== '' ? (currentUser?.id || currentUser?.uuid || null) : null
        };
      });

    // 5. 送信
    postData('/api/post_update_report_progress_powerplant_bulk', { bulk: bulkUpdates })
      .then(res => {
        if (!res || res.success === false) {
          setSuiroExtractList(prevValues);
          console.error(`${field} time update failed`, res);
        }
      })
      .catch(err => {
        console.error(`${field} time update error`, err);
        setSuiroExtractList(prevValues);
      });

  }, [suiroExtractList, currentUser, setSuiroExtractList, toggleColumn]);

  const handleRowSelectChange = useCallback((uuid, field, value, is_all) => {
    const currentRow = suiroExtractList?.find(r => r.uuid === uuid);
    if (!currentRow) {
      toggleColumn?.(field, true);
      return;
    }

    const prevValues = suiroExtractList.map(r => ({ ...r }));
    // 未選択表現 ("" or "-") を空文字に統一
    const newValForUI = (value === '' || value === '-') ? '' : value;

    // 1. 更新対象のUUIDリストを確定 (表示されている範囲に限定)
    const targetUuids = is_all
      ? suiroExtractList.map(r => r.report_progress_powerplant_uuid)
      : [currentRow.report_progress_powerplant_uuid];

    // ヘルパー: 連動フラグの計算ロジック
    const getLinkedUpdates = (row, currentField, currentVal) => {
      let updates = { [currentField]: currentVal };

      // 点検員連動
      if (currentField === 'canalinspector1' || currentField === 'canalinspector2') {
        const v1 = currentField === 'canalinspector1' ? currentVal : (row.canalinspector1 ?? '');
        const v2 = currentField === 'canalinspector2' ? currentVal : (row.canalinspector2 ?? '');
        updates.is_canalinspector = !!v1 || !!v2;
      }
      // 内部点検者連動
      else if (currentField === 'perform_internal_inspection_user1' || currentField === 'perform_internal_inspection_user2') {
        const v1 = currentField === 'perform_internal_inspection_user1' ? currentVal : (row.perform_internal_inspection_user1 ?? '');
        const v2 = currentField === 'perform_internal_inspection_user2' ? currentVal : (row.perform_internal_inspection_user2 ?? '');
        updates.is_perform_internal_inspection_user = !!v1 || !!v2;
      }
      return updates;
    };

    // 2. UI更新 (Optimistic Update)
    setSuiroExtractList(prev => prev.map(row => {
      if (!targetUuids.includes(row.report_progress_powerplant_uuid)) return row;

      const updates = getLinkedUpdates(row, field, newValForUI);
      let updatedRow = { ...row, ...updates };

      // 担当者情報の更新
      const updatedByField = `${field}_by`;
      updatedRow[updatedByField] = newValForUI !== '' ? (currentUser?.id || currentUser?.uuid || null) : null;

      return updatedRow;
    }));

    // 3. APIペイロード作成
    const bulkUpdates = suiroExtractList
      .filter(row => targetUuids.includes(row.report_progress_powerplant_uuid))
      .map(row => {
        const updates = getLinkedUpdates(row, field, newValForUI);
        // DB送信用に空文字を null に変換
        Object.keys(updates).forEach(key => {
          if (updates[key] === '') updates[key] = null;
        });

        return {
          uuid: row.report_progress_powerplant_uuid,
          updates: updates,
          updated_by: newValForUI !== '' ? (currentUser?.id || currentUser?.uuid || null) : null
        };
      });

    // 4. 送信 (常に bulk API を使用)
    postData('/api/post_update_report_progress_powerplant_bulk', { bulk: bulkUpdates })
      .then(res => {
        if (!res || res.success === false) {
          setSuiroExtractList(prevValues);
          console.error(`${field} select update failed`, res);
        }
      })
      .catch(err => {
        console.error(`${field} select update error`, err);
        setSuiroExtractList(prevValues);
      });

  }, [suiroExtractList, currentUser, setSuiroExtractList, toggleColumn]);

  return {
    clearAllRows,
    handleReset,
    handleCheckboxChange,
    handleTextFieldChange,
    handleRowTimeChange,
    handleRowSelectChange
  };
};